'use client';

import * as React from 'react';
import Image from 'next/image';
import NiceModal from '@ebay/nice-modal-react';
import { ImageIcon, XIcon } from 'lucide-react';

import { Button } from '@workspace/ui/components/button';
import { Card, CardContent } from '@workspace/ui/components/card';
import { cn } from '@workspace/ui/lib/utils';

import { FullscreenImageModal } from '~/components/generator/shared/fullscreen-image-modal';
import type { PoseDto } from '~/types/dtos/pose-dto';

type PoseCardProps = {
  item: PoseDto;
  onSelect: (pose: PoseDto) => void;
  isSelected: boolean;
  disabled?: boolean;
};

export function PoseCard({
  item: pose,
  onSelect,
  isSelected,
  disabled
}: PoseCardProps): React.JSX.Element {
  const [isHovered, setIsHovered] = React.useState(false);
  console.log('pose', pose);
  const imageUrl = pose.image?.url;

  return (
    <Card
      className={cn(
        'group h-48 w-full p-0 transition-all md:h-64 overflow-hidden',
        isSelected && 'border-2 border-primary',
        disabled
          ? 'cursor-not-allowed opacity-50'
          : 'cursor-pointer hover:shadow-md'
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={
        disabled
          ? undefined
          : () => {
              onSelect(pose);
            }
      }
    >
      <CardContent className="h-full p-0">
        <div className="relative h-full w-full overflow-hidden rounded-lg">
          {imageUrl ? (
            <>
              <Image
                src={imageUrl}
                alt={pose.title}
                width={500}
                height={500}
                className={cn(
                  'absolute inset-0 h-full w-full object-cover transition-all duration-300',
                  isHovered ? 'scale-105' : 'scale-100'
                )}
              />
            </>
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-muted">
              <span className="text-sm text-muted-foreground">No image</span>
            </div>
          )}

          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />

          <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
            <div className="space-y-2">
              <div>
                <h4 className="line-clamp-1 text-sm font-medium">
                  {pose.title}
                </h4>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

type SelectedPoseCardProps = {
  item: PoseDto;
  onRemove?: (id: string) => void;
};

export function SelectedPoseCard({
  item: pose,
  onRemove
}: SelectedPoseCardProps): React.JSX.Element {
  const imageUrl = pose.image?.url;

  return (
    <Card
      className="group relative h-12 w-12 cursor-pointer p-0 transition-all hover:shadow-md md:h-18 md:w-18"
      onClick={() => {
        if (imageUrl) {
          NiceModal.show(FullscreenImageModal, {
            images: [
              {
                url: imageUrl,
                title: pose.title,
                filename: `pose-${new Date().toISOString().split('T')[0]}.jpg`
              }
            ],
            currentIndex: 0
          });
        }
      }}
    >
      <CardContent className="h-full p-0">
        <div className="relative h-full w-full overflow-hidden rounded-lg">
          {imageUrl ? (
            <>
              <Image
                src={imageUrl}
                alt={pose.title}
                width={72}
                height={72}
                className="absolute inset-0 h-full w-full object-cover object-top transition-transform group-hover:scale-105"
              />
            </>
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-muted">
              <span className="text-sm text-muted-foreground">
                <ImageIcon className="h-4 w-4" />
              </span>
            </div>
          )}

          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
        </div>
      </CardContent>
      {onRemove && (
        <div className="absolute right-0 top-0 opacity-0 transition-opacity group-hover:opacity-100 sm:right-1 sm:top-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              onRemove(pose.id);
            }}
            className="h-12 w-12 bg-background/80 p-0 hover:bg-background hover:text-destructive sm:h-6 sm:w-6"
            title="Remove pose"
          >
            <XIcon className="h-4 w-4" />
          </Button>
        </div>
      )}
    </Card>
  );
}
